import React, { useState, useEffect, useRef } from 'react';
import { useRefinementList } from 'react-instantsearch';
import Open from '../../images/chevron-down.svg';
import Checkmark from '../../images/checkmark-w.svg';
import CheckmarkDark from '../../images/checkmark-b.svg';
import { useOnClickOutside } from 'usehooks-ts';
import Translate from '../Translate/Translate';
import { translate } from '../../services/Translations';
import classNames from 'classnames';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  MagnifyingGlassIcon,
  MinusIcon,
} from '@heroicons/react/20/solid';

interface AlgoliaFacetRefinementProps {
  facetName: string;
  showSearch?: boolean;
}

const LETTER_SIZE_ORDER: Record<string, number> = {
  '4XS': 5,
  XXXXS: 5,
  '3XS': 10,
  XXXS: 10,
  '2XS': 20,
  XXS: 20,
  '2XS/XS': 25,
  XS: 30,
  'XS/S': 35,
  S: 40,
  'S/M': 45,
  M: 50,
  'M/L': 55,
  L: 60,
  'L/XL': 65,
  XL: 70,
  'XL/2XL': 75,
  'XL/XXL': 75,
  '2XL': 80,
  XXL: 80,
  '2XL/3XL': 85,
  'XXL/XXXL': 85,
  '3XL': 90,
  XXXL: 90,
  '4XL': 100,
  XXXXL: 100,
  '5XL': 110,
  XXXXXL: 110,
  '6XL': 120,
  XXXXXXL: 120,
};

type ParsedSize = {
  start: number;
  end: number;
};

const UNICODE_FRACTIONS: Record<string, number> = {
  '½': 0.5,
  '⅓': 1 / 3,
  '⅔': 2 / 3,
  '¼': 0.25,
  '¾': 0.75,
  '⅛': 0.125,
  '⅜': 0.375,
  '⅝': 0.625,
  '⅞': 0.875,
};

const parseSingleNumber = (str: string): number | null => {
  const trimmed = str.trim();
  if (!trimmed) return null;

  // 1. Unicode fractions e.g. "42 ½", "42⅓", "½"
  for (const [char, val] of Object.entries(UNICODE_FRACTIONS)) {
    if (trimmed.includes(char)) {
      const baseStr = trimmed.replace(char, '').trim();
      const base = baseStr ? parseFloat(baseStr.replace(',', '.')) : 0;
      if (!isNaN(base)) {
        return base + val;
      }
    }
  }

  // 2. Mixed fraction e.g. "42 2/3", "42 1/3", "42 1/2"
  const mixedMatch = trimmed.match(/^(\d+(?:[.,]\d+)?)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixedMatch) {
    const whole = parseFloat(mixedMatch[1].replace(',', '.'));
    const num = parseFloat(mixedMatch[2]);
    const den = parseFloat(mixedMatch[3]);
    if (den !== 0 && !isNaN(whole) && !isNaN(num) && !isNaN(den)) {
      return whole + num / den;
    }
  }

  // 3. Known small standalone fractions e.g. "1/2", "1/3", "2/3", "1/4", "3/4"
  const fractionMatch = trimmed.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (fractionMatch) {
    const num = parseFloat(fractionMatch[1]);
    const den = parseFloat(fractionMatch[2]);
    if (
      den !== 0 &&
      ((num === 1 && [2, 3, 4, 8].includes(den)) ||
        (num === 2 && den === 3) ||
        (num === 3 && den === 4))
    ) {
      return num / den;
    }
  }

  // 4. Standard decimal / integer e.g. "42", "42.5", "42,5"
  const decimalMatch = trimmed.match(/^(\d+(?:[.,]\d+)?)/);
  if (decimalMatch) {
    const val = parseFloat(decimalMatch[1].replace(',', '.'));
    if (!isNaN(val)) {
      return val;
    }
  }

  return null;
};

const parseSizeRange = (str: string): ParsedSize | null => {
  const trimmed = str.trim();
  if (!trimmed) return null;

  // Check if it's a range like "34/38", "38/42", "34-38", "34 - 38", "110/116"
  const isMixedFraction = /^\d+(?:[.,]\d+)?\s+\d+\s*\/\s*\d+$/.test(trimmed);
  if (!isMixedFraction) {
    const rangeMatch = trimmed.match(
      /^([0-9.,\s½⅓⅔¼¾⅛⅜⅝⅞]+?)\s*[\/\-–—]\s*([0-9.,\s½⅓⅔¼¾⅛⅜⅝⅞]+)$/,
    );
    if (rangeMatch) {
      const startNum = parseSingleNumber(rangeMatch[1]);
      const endNum = parseSingleNumber(rangeMatch[2]);
      if (startNum !== null && endNum !== null) {
        return { start: startNum, end: endNum };
      }
    }
  }

  const single = parseSingleNumber(trimmed);
  if (single !== null) {
    return { start: single, end: single };
  }

  return null;
};

const compareSizes = (a: any, b: any): number => {
  const aText = (a?.name || a?.label || a?.value || '').toString().trim();
  const bText = (b?.name || b?.label || b?.value || '').toString().trim();

  const aNorm = aText.toUpperCase().replace(/\s+/g, '');
  const bNorm = bText.toUpperCase().replace(/\s+/g, '');

  const aOrder = LETTER_SIZE_ORDER[aNorm];
  const bOrder = LETTER_SIZE_ORDER[bNorm];

  const aIsLetter = aOrder !== undefined;
  const bIsLetter = bOrder !== undefined;

  if (aIsLetter && bIsLetter) {
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return aText.localeCompare(bText);
  }

  if (aIsLetter) return -1;
  if (bIsLetter) return 1;

  const aRange = parseSizeRange(aText);
  const bRange = parseSizeRange(bText);

  if (aRange !== null && bRange !== null) {
    if (aRange.start !== bRange.start) {
      return aRange.start - bRange.start;
    }
    if (aRange.end !== bRange.end) {
      return aRange.end - bRange.end;
    }
    return aText.localeCompare(bText);
  }

  if (aRange !== null) return -1;
  if (bRange !== null) return 1;

  return aText.localeCompare(bText, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const DEFAULT_SORT_BY: ['isRefined:desc', 'count'] = [
  'isRefined:desc',
  'count',
];

const AlgoliaFacetRefinement = ({
  facetName,
  showSearch = true,
}: AlgoliaFacetRefinementProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const {
    items,
    isShowingMore,
    canToggleShowMore,
    toggleShowMore,
    refine,
    searchForItems,
  } = useRefinementList({
    attribute: facetName,
    showMore: true,
    limit: 7,
    showMoreLimit: 450,
    sortBy: (facetName === 'size' ? compareSizes : DEFAULT_SORT_BY) as any,
  });
  const [isActive, setIsActive] = useState(false);
  const filterMenuRef = useRef<HTMLDivElement>(null);
  const [translatedPlaceholder, setTranslatedPlaceholder] = useState('');

  let visibleItems = items;

  useEffect(() => {
    searchForItems(searchTerm);
  }, [searchTerm]);

  const handleClickOutside = () => {
    setIsActive(false);
  };

  useOnClickOutside(
    filterMenuRef as React.RefObject<HTMLElement>,
    handleClickOutside,
  );

  const itemIsRefined = visibleItems.filter((item) => item.isRefined).length;

  useEffect(() => {
    const translatePlaceholder = async () => {
      const translated =
        facetName === 'size'
          ? await translate('facet-refinement', 'find-size')
          : facetName === 'shops'
            ? await translate('facet-refinement', 'find-shop')
            : facetName === 'brand'
              ? await translate('facet-refinement', 'find-brand')
              : facetName === 'color'
                ? await translate('facet-refinement', 'find-color')
                : '';
      setTranslatedPlaceholder(translated || '');
    };

    translatePlaceholder();
  }, []);
  return (
    <div
      ref={filterMenuRef}
      className={classNames(
        'relative border-b lg:border-none lg:mr-3 cursor-pointer',
        {
          'pb-3 lg:pb-0': isActive,
        },
      )}
    >
      <div
        onClick={() => setIsActive(!isActive)}
        className={classNames(
          'flex py-2 items-center justify-between border-rgrey-light cursor-pointer w-full lg:w-fit lg:border lg:rounded-sm lg:px-3',
          {
            'border-black': itemIsRefined > 0,
          },
        )}
      >
        <p
          className={classNames(
            'font-axi-bold text-xl leading-8 text-black uppercase lg:normal-case lg:text-s lg:leading-4 lg:font-axi-book',
            {
              'font-axi-bold!': itemIsRefined > 0,
            },
          )}
        >
          {facetName === 'size' ? (
            <Translate component="facet-refinement" keyword="size" />
          ) : facetName === 'brand' ? (
            <Translate component="facet-refinement" keyword="brand" />
          ) : facetName === 'color' ? (
            <Translate component="facet-refinement" keyword="color" />
          ) : facetName === 'shops' ? (
            <Translate component="facet-refinement" keyword="shops" />
          ) : (
            facetName === 'rvsProductActivities' && (
              <Translate component="facet-refinement" keyword="tags" />
            )
          )}
        </p>
        <div className="flex">
          {itemIsRefined > 0 && (
            <div className="bg-black text-white px-2 flex items-center rounded-sm label font-axi-bold mr-6 lg:ml-2 lg:mr-0">
              {itemIsRefined}
            </div>
          )}
          {isActive ? (
            <>
              <ChevronUpIcon className="hidden lg:block lg:w-5 lg:h-5 lg:ml-2" />
              <MinusIcon className="lg:hidden w-6 h-6" />
            </>
          ) : (
            <ChevronDownIcon className="lg:w-5 lg:h-5 w-6 h-6 lg:ml-2" />
          )}
        </div>
      </div>
      {isActive && (
        <div className="h-fit overflow-x-auto max-h-72 lg:mt-0.5 lg:pt-6 lg:px-4 lg:pb-7 lg:bg-white lg:border lg:border-rgrey-light lg:rounded-sm lg:w-83.75 lg:max-h-96 lg:absolute lg:z-10">
          {showSearch && (
            <div className="relative">
              <input
                className="rounded-sm w-full p-2 bg-rgrey-light border-none focus:outline-hidden focus:ring-0"
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                }}
                placeholder={translatedPlaceholder}
              />
              <MagnifyingGlassIcon className="w-5 h-5 absolute right-2 top-2 z-10" />
            </div>
          )}
          <div
            className={classNames('flex px-0.5 flex-wrap gap-3 justify-start', {
              'mt-4': showSearch, // Add margin top only if search is shown
            })}
          >
            {visibleItems.map((item) => (
              <button
                onClick={() => refine(item.value)}
                className="relative"
                key={item.value}
              >
                {item.isRefined && facetName === 'color' && (
                  <img
                    className="px-2 absolute top-2.5 stroke-black"
                    src={
                      item.label === 'Valge'
                        ? CheckmarkDark
                        : item.label === 'Белый'
                          ? CheckmarkDark
                          : Checkmark
                    }
                  />
                )}
                {facetName === 'color' ? (
                  <div
                    className={classNames('w-8 h-8 rounded-full', {
                      'bg-black':
                        item.label === 'Must' || item.label === 'Черный',
                      'bg-white border':
                        item.label === 'Valge' || item.label === 'Белый',
                      'bg-blue-500':
                        item.label === 'Sinine' || item.label === 'Cиний',
                      'bg-gray-500':
                        item.label === 'Hall' || item.label === 'Серый',
                      'bg-pink-500':
                        item.label === 'Roosa' || item.label === 'Розовый',
                      'bg-[#7ABC12]':
                        item.label === 'Roheline' || item.label === 'Зеленый',
                      'bg-[#FF4B4B]':
                        item.label === 'Punane' || item.label === 'Красный',
                      'bg-[#d7c7a5]':
                        item.label === 'Beež' || item.label === 'Бежевый',
                      'bg-[#9747FF]':
                        item.label === 'Lilla' || item.label === 'Лиловый',
                      'bg-[#FF8B00]':
                        item.label === 'Oranž' || item.label === 'Оранжевый',
                      'bg-[#FFDF00]':
                        item.label === 'Kollane' || item.label === 'Желтый',
                      'bg-[#C6997B]':
                        item.label === 'Pruun' || item.label === 'Коричневый',
                    })}
                  ></div>
                ) : (
                  <div>
                    <div
                      className={classNames('border rounded-sm px-4 py-2', {
                        'bg-black text-white border-black': item.isRefined,
                      })}
                    >
                      {item.label}
                    </div>
                  </div>
                )}
              </button>
            ))}

            {canToggleShowMore && (
              <button
                className="w-full pt-3 flex justify-center"
                onClick={() => toggleShowMore()}
              >
                <p className="text-base leading-6 text-black font-axi-bold mr-4">
                  {isShowingMore ? (
                    <Translate
                      component="facet-refinement"
                      keyword="show-less"
                    />
                  ) : (
                    <Translate
                      component="facet-refinement"
                      keyword="show-more"
                    />
                  )}
                </p>
                <img
                  className={`w-6 h-6 ${
                    isShowingMore ? 'origin-center rotate-180' : ''
                  }`}
                  src={Open}
                />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AlgoliaFacetRefinement;
